// CloudflareTutorial.tsx - Cloudflare API Key Tutorial
// Spec 14: Multi-Cloud Integration

import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import { useState } from 'react'

interface FAQItem {
  question: string
  answer: string
}

const FAQ_ITEMS: FAQItem[] = [
  {
    question: 'API token nerede bulabilirim?',
    answer: 'Cloudflare Dashboard > My Profile > API Tokens bolumunden "Create Token" butonuna tiklayin.'
  },
  {
    question: 'Hangi izinler gerekli?',
    answer: 'Workers Scripts:Edit, Account Settings:Read, D1:Edit, R2:Edit izinleri gereklidir.'
  },
  {
    question: 'Account ID nerede?',
    answer: 'Cloudflare Dashboard > herhangi bir domain > sag tarafta "Account ID" gorunur.'
  }
]

export default function CloudflareTutorial() {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div className="min-h-screen" style={{ background: 'var(--body-bg)', padding: '20px' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
          Cloudflare API Key Nasil Alinir?
        </h1>
        <p className="mb-8" style={{ color: 'var(--text-secondary)' }}>
          Adim adim rehber
        </p>

        {/* Steps */}
        <div className="space-y-6 mb-8">
          {/* Step 1 */}
          <div className="card p-6 rounded" style={{ background: 'var(--card-bg)' }}>
            <div className="flex items-start gap-4">
              <div
                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold"
                style={{ background: 'var(--primary-color)', color: 'white' }}
              >
                1
              </div>
              <div className="flex-1">
                <h3 className="font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                  Cloudflare Dashboard&apos;a gidin
                </h3>
                <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
                  <a
                    href="https://dash.cloudflare.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1"
                    style={{ color: 'var(--primary-color)' }}
                  >
                    dash.cloudflare.com
                    <ExternalLink size={14} />
                  </a>
                  {' '}adresine gidin ve giris yapin.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="card p-6 rounded" style={{ background: 'var(--card-bg)' }}>
            <div className="flex items-start gap-4">
              <div
                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold"
                style={{ background: 'var(--primary-color)', color: 'white' }}
              >
                2
              </div>
              <div className="flex-1">
                <h3 className="font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                  API Tokens sayfasina gidin
                </h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Sag ust kosede profil ikonuna tiklayin → My Profile → API Tokens
                </p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="card p-6 rounded" style={{ background: 'var(--card-bg)' }}>
            <div className="flex items-start gap-4">
              <div
                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold"
                style={{ background: 'var(--primary-color)', color: 'white' }}
              >
                3
              </div>
              <div className="flex-1">
                <h3 className="font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                  Create Token butonuna tiklayin
                </h3>
                <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
                  &quot;Create Custom Token&quot; secenegini secin.
                </p>
                <div
                  className="p-3 rounded text-sm"
                  style={{ background: 'var(--info-bg)', color: 'var(--info-color)' }}
                >
                  Gerekli izinler: Workers Scripts:Edit, Account Settings:Read, D1:Edit, R2:Edit
                </div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="card p-6 rounded" style={{ background: 'var(--card-bg)' }}>
            <div className="flex items-start gap-4">
              <div
                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold"
                style={{ background: 'var(--primary-color)', color: 'white' }}
              >
                4
              </div>
              <div className="flex-1">
                <h3 className="font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                  Account ID&apos;yi alin
                </h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Ana sayfada herhangi bir domain&apos;e tiklayin → sag tarafta &quot;Account ID&quot; gorunur.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Accordion */}
        <div className="card p-6 rounded" style={{ background: 'var(--card-bg)' }}>
          <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            Sik Sorulan Sorular
          </h3>
          <div className="space-y-2">
            {FAQ_ITEMS.map((item, index) => (
              <div
                key={index}
                className="rounded overflow-hidden"
                style={{ border: '1px solid var(--border-color)' }}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left transition-colors hover:bg-opacity-50"
                  style={{ background: 'var(--secondary-bg)' }}
                >
                  <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {item.question}
                  </span>
                  {openIndex === index ? (
                    <ChevronUp size={20} style={{ color: 'var(--text-secondary)' }} />
                  ) : (
                    <ChevronDown size={20} style={{ color: 'var(--text-secondary)' }} />
                  )}
                </button>
                {openIndex === index && (
                  <div className="p-4" style={{ background: 'var(--card-bg)' }}>
                    <p style={{ color: 'var(--text-secondary)' }}>{item.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
